"use client";

import { useState } from "react";

import { useHubspotContext } from "@/components/forms/hubspot_context";
import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";

interface VsEmailCaptureProps {
  className?: string;
}

const FIELD =
  "h-11 w-full rounded-lg border border-[#313335] bg-[#171717] px-4 text-[14px] text-white placeholder:text-white/40 transition-colors focus:border-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60";
const LABEL = "mb-2 block text-[13px] font-medium text-white/70";

// HubSpot form GUID for the New York register-interest capture. The portal ID
// and access token are applied server-side in /api/submit-hubspot-form.
const HUBSPOT_FORM_GUID = "6e96075b-80f1-46a1-92c4-3ccb16e3d7a0";
const FORM_NAME = "Visionaries Summit New York register interest";

/**
 * Email capture — the page's primary forward-looking conversion. Collects
 * first name, last name and email and submits to HubSpot (firstname /
 * lastname / email) via the first-party /api/submit-hubspot-form route, which
 * proxies to HubSpot server-side (avoids ad-blocker / CORS issues with calling
 * api.hsforms.com directly from the browser).
 *
 * Designed to sit inside a `PageRailSection` (the parent adds the horizontal
 * gutter).
 */
export function VsEmailCapture({ className }: VsEmailCaptureProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  // Builds the HubSpot context including the `hubspotutk` tracking cookie (so
  // submissions link to existing contacts) and any UTM / referral params.
  const hubspotContext = useHubspotContext();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const firstname = String(data.get("firstName") ?? "").trim();
    const lastname = String(data.get("lastName") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();

    setSubmitting(true);
    setError(false);
    try {
      const res = await fetch("/api/submit-hubspot-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          guid: HUBSPOT_FORM_GUID,
          formName: FORM_NAME,
          data: { firstname, lastname, email },
          hubspotContext: { ...hubspotContext, pageName: FORM_NAME },
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      id="register"
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className={cn("scroll-mt-[120px] py-[72px] max-md:py-12", className)}
    >
      <p className="mb-3 text-[12px] tracking-[0.0967em] text-[#95999d] uppercase">
        register
      </p>
      <h2 className="mb-3 text-[40px] leading-[1.2] font-normal tracking-[-1.6px] text-white max-md:text-[32px] max-md:tracking-[-1.28px]">
        Don&apos;t want to miss out on future events?
      </h2>
      <p className="mb-10 max-w-[720px] text-[14px] leading-[1.6] text-white/70 max-md:mb-8">
        That&apos;s a wrap on London and New York. Register your interest to be
        first to hear where the Visionaries Summit lands next.
      </p>

      {submitted ? (
        <div className="max-w-[720px] rounded-[20px] border border-[#313335] bg-[#171717] p-8">
          <h3 className="mb-2 text-[20px] font-normal tracking-[-0.01em] text-white">
            Thanks, you&apos;re on the list.
          </h3>
          <p className="text-[14px] leading-[1.6] text-white/70">
            We&apos;ll be in touch as soon as the next Visionaries Summit is
            announced.
          </p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="max-w-[720px]"
          noValidate={false}
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="vs-first-name" className={LABEL}>
                First name
              </label>
              <input
                id="vs-first-name"
                name="firstName"
                type="text"
                required
                placeholder="First name"
                className={FIELD}
              />
            </div>
            <div>
              <label htmlFor="vs-last-name" className={LABEL}>
                Last name
              </label>
              <input
                id="vs-last-name"
                name="lastName"
                type="text"
                required
                placeholder="Last name"
                className={FIELD}
              />
            </div>
          </div>

          <div className="mt-5">
            <label htmlFor="vs-email" className={LABEL}>
              Email address
            </label>
            <input
              id="vs-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@company.com"
              className={FIELD}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={cn(
              "mt-8 inline-flex h-11 items-center justify-center gap-1.5 rounded-full bg-[#001eff] px-6 text-[16px] font-medium text-white",
              "transition-colors hover:bg-[#1a1aff] motion-reduce:transition-none",
              "disabled:cursor-not-allowed disabled:opacity-60",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
            )}
          >
            {submitting ? "Submitting…" : "Be the first to hear"}
            {!submitting ? <span aria-hidden>→</span> : null}
          </button>

          {error ? (
            <p className="mt-4 text-[13px] text-[#ff8a7a]">
              Something went wrong. Please try again, or email us at{" "}
              <a
                href="mailto:marketing@wayflyer.com"
                className="underline underline-offset-2"
              >
                marketing@wayflyer.com
              </a>
              .
            </p>
          ) : null}
        </form>
      )}

      <p className="mt-6 max-w-[720px] text-[13px] leading-[1.6] text-white/45">
        Interested in speaking or partnering? Drop us an email at{" "}
        <a
          href="mailto:marketing@wayflyer.com"
          className="underline underline-offset-2 transition-colors hover:text-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          marketing@wayflyer.com
        </a>
        .
      </p>
    </div>
  );
}
